// Zero-dependency so it's safe to import from Edge middleware as well as Node/browser code.
//
// JWTs are base64url-encoded (RFC 4648 §5: '-'/'_' instead of '+'/'/', no padding), but
// `atob` only implements standard base64. Decoding a JWT payload with plain `atob` throws
// whenever the payload happens to contain a '-' or '_' — which, for any realistically-sized
// payload, is close to guaranteed — silently misclassifying a perfectly valid, unexpired
// token as unparseable/expired everywhere the caller catches the exception.
function base64UrlToBase64(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const paddingNeeded = (4 - (base64.length % 4)) % 4
  return base64 + '='.repeat(paddingNeeded)
}

// `atob` decodes base64 into a byte string (one JS char per byte, i.e. Latin-1) — it does
// NOT reassemble multi-byte UTF-8 sequences. Feeding that straight into JSON.parse works
// by coincidence for ASCII-only claims (the only kind this app's tokens carry today: sub,
// exp, email_verified) but silently mangles any non-ASCII character (e.g. a name/email
// with accents) into mojibake instead of throwing, so it's worth decoding properly here.
function atobToUtf8(binary: string): string {
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/** Decode a JWT's payload (second segment) without verifying its signature. */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    return JSON.parse(atobToUtf8(atob(base64UrlToBase64(parts[1])))) as T
  } catch {
    return null
  }
}
